import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import {
  AdGroupSyncPreviewRequestDto,
  CreateAdGroupProductMappingDto,
  CreateAdGroupRoleMappingDto,
  UpdateAdGroupProductMappingDto,
  UpdateAdGroupRoleMappingDto,
} from "@/types/auth";

import { queryKeys } from "./queryKeys";

export const useAdGroupsQuery = () => {
  const { getAdGroups } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.AD_GROUPS, currentCompany.id],
    queryFn: () => getAdGroups(currentCompany.id),
    staleTime: 30000,
  });
};

export const useAdGroupProductMappingsQuery = () => {
  const { getAdGroupProductMappings } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.AD_GROUP_PRODUCT_MAPPINGS, currentCompany.id],
    queryFn: () => getAdGroupProductMappings(currentCompany.id),
    staleTime: 30000,
  });
};

export const useAdGroupRoleMappingsQuery = () => {
  const { getAdGroupRoleMappings } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.AD_GROUP_ROLE_MAPPINGS, currentCompany.id],
    queryFn: () => getAdGroupRoleMappings(currentCompany.id),
    staleTime: 30000,
  });
};

export const useCreateAdGroupProductMappingMutation = () => {
  const { createAdGroupProductMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAdGroupProductMappingDto) =>
      createAdGroupProductMapping(currentCompany.id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_PRODUCT_MAPPINGS, currentCompany.id],
      });
    },
  });
};

export const useUpdateAdGroupProductMappingMutation = () => {
  const { updateAdGroupProductMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: number; dto: UpdateAdGroupProductMappingDto }) =>
      updateAdGroupProductMapping(currentCompany.id, variables.id, variables.dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_PRODUCT_MAPPINGS, currentCompany.id],
      });
    },
  });
};

export const useDeleteAdGroupProductMappingMutation = () => {
  const { deleteAdGroupProductMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteAdGroupProductMapping(currentCompany.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_PRODUCT_MAPPINGS, currentCompany.id],
      });
    },
  });
};

export const useCreateAdGroupRoleMappingMutation = () => {
  const { createAdGroupRoleMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateAdGroupRoleMappingDto) =>
      createAdGroupRoleMapping(currentCompany.id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_ROLE_MAPPINGS, currentCompany.id],
      });
    },
  });
};

export const useUpdateAdGroupRoleMappingMutation = () => {
  const { updateAdGroupRoleMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: number; dto: UpdateAdGroupRoleMappingDto }) =>
      updateAdGroupRoleMapping(currentCompany.id, variables.id, variables.dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_ROLE_MAPPINGS, currentCompany.id],
      });
    },
  });
};

export const useDeleteAdGroupRoleMappingMutation = () => {
  const { deleteAdGroupRoleMapping } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteAdGroupRoleMapping(currentCompany.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_ROLE_MAPPINGS, currentCompany.id],
      });
    },
  });
};

/** The stored-configuration preview. Only fetched while a dialog that shows it is open. */
export const useAdGroupSyncPreviewQuery = (enabled: boolean) => {
  const { getAdGroupSyncPreview } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.AD_GROUP_SYNC_PREVIEW, currentCompany.id],
    queryFn: () => getAdGroupSyncPreview(currentCompany.id),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
};

/**
 * The impact of a configuration the engineer is still editing. The serialised body is debounced
 * into the query key, so keystrokes collapse into one planner run, an out-of-order answer can
 * never overwrite a newer one, and going back to an earlier form state is served from the cache.
 */
export const useAdGroupSyncCandidatePreviewQuery = (
  request: AdGroupSyncPreviewRequestDto | undefined,
) => {
  const { postAdGroupSyncPreview } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const [body] = useDebounce(request ? JSON.stringify(request) : "", 500);

  return useQuery({
    queryKey: [queryKeys.AD_GROUP_SYNC_CANDIDATE_PREVIEW, currentCompany.id, body],
    queryFn: () => postAdGroupSyncPreview(currentCompany.id, JSON.parse(body)),
    enabled: body.length > 0,
    staleTime: Infinity,
    retry: false,
  });
};

/** Polls only while the queue is draining; 404 means SCIM is disabled, so it must not retry. */
export const useAdGroupSyncStatusQuery = () => {
  const { getAdGroupSyncStatus } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.AD_GROUP_SYNC_STATUS, currentCompany.id],
    queryFn: () => getAdGroupSyncStatus(currentCompany.id),
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 2000 : false),
    retry: false,
  });
};

export const useTriggerAdGroupSyncMutation = () => {
  const { triggerAdGroupSync } = useRestApi().adGroups;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerAdGroupSync(currentCompany.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AD_GROUP_SYNC_STATUS, currentCompany.id],
      });
    },
  });
};
